/* The alert-routing policy: one read, one create, one delete. D-17.
 *
 * **No PUT, because the API has none.** Migration 0071 grants no `UPDATE`, so a destination is
 * replaced rather than edited — the audit chain then carries both halves of a change, and "when did
 * our alerts start going there" is answerable from the chain rather than inferable from a row's
 * current value. A UI offering an edit would be offering something the server refuses.
 *
 * The read is invalidated after both mutations rather than patched optimistically. A declaration can
 * be refused server-side — a destination outside the deployment's allow-list is a 422, and the
 * ceiling is a 409 — so the authoritative answer is the one the server just wrote. An optimistic
 * patch would show a routing rule that does not exist, on the screen whose subject is where alerts
 * actually go.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isNotificationRule,
  isRoutingPolicy,
} from '@/shared/api/generated/observability';
import type {
  NotificationRule,
  NotificationRuleWrite,
  RoutingPolicy,
} from '@/shared/api/generated/observability';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  postNoContent,
  remove,
} from '@/shared/api/http';

export const ROUTING_URL = '/v1/observability/notification-rules';

const QUERY_KEY = ['observability', 'notification-rules'] as const;

async function fetchPolicy(): Promise<RoutingPolicy> {
  /* **One guard, because the generated envelope guard now validates its items.** `isRoutingPolicy`
   * emits `(candidate['rules'] as readonly unknown[]).every((item) => isNotificationRule(item))`, so
   * the hand-written loop this file carried for one commit is redundant — and S-71's whole point is
   * that the loop should not have to be remembered per slice. `anomaly`, `evals`, `lineage` and
   * `profiler` still carry theirs; they are harmless double-checks and safe to remove. */
  return getOne(ROUTING_URL, isRoutingPolicy, 'routing policy');
}

export function useRoutingPolicy(): UseQueryResult<RoutingPolicy> {
  return useQuery({ queryKey: QUERY_KEY, queryFn: fetchPolicy, retry: false });
}

export function useDeclareRule(): UseMutationResult<
  NotificationRule,
  Error,
  NotificationRuleWrite
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (write: NotificationRuleWrite) =>
      post(
        ROUTING_URL,
        /* Keyed on the rule's own content, so a retried submission of the same rule carries the same
         * key and a genuinely different rule gets a new one. Keying on a constant would make the
         * second rule a replay of the first; minting inside the handler would make a double-click
         * two unrelated rules. */
        idempotencyKeyFor('notification-rule', JSON.stringify(write)),
        isNotificationRule,
        'notification rule',
        write,
      ),
    onSuccess: (_result, write) => {
      forgetIdempotencyKey('notification-rule', JSON.stringify(write));
      void client.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useWithdrawRule(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) =>
      remove(`${ROUTING_URL}/${encodeURIComponent(ruleId)}`),
    /* `onSettled`, not `onSuccess`: a 404 means somebody else withdrew it, and the list is stale
     * either way. Refetching on failure is what stops the screen showing a rule the server has
     * already forgotten. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/** Switch one rule off without losing it, or bring it back.
 *
 * **`POST …/enabled` carrying the state, not two verbs.** `enabled: false` sent twice leaves the rule
 * in the same state, where a `/disable` endpoint would invite a client to reason about whether it
 * already fired. Migration 0085 grants `UPDATE (enabled)` and nothing wider, so this is the only
 * field of a rule that can move: a destination is still replaced rather than edited, and the audit
 * chain still carries both halves of a change.
 *
 * **Disabling is not withdrawing**, which is why the control exists at all. Withdrawing loses the
 * destination, so restoring it means retyping an address that has to clear the deployment's
 * allow-list again, and one intent's audit trail ends up split across two rule ids. A maintenance
 * window is the ordinary case.
 *
 * Invalidated rather than patched optimistically, matching the other two mutations: the coverage
 * readout is recomputed from every rule, so a patched cache would show a screen whose headline and
 * whose table disagreed for a moment — on exactly the claim this screen exists to make.
 */
export interface RuleEnabledChange {
  readonly ruleId: string;
  readonly enabled: boolean;
}

export function useSetRuleEnabled(): UseMutationResult<void, Error, RuleEnabledChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: RuleEnabledChange) =>
      postNoContent(
        `${ROUTING_URL}/${encodeURIComponent(change.ruleId)}/enabled`,
        /* Keyed on the rule *and* the state it is being moved to, so a retried request carries the
         * same key while disabling and re-enabling are two decisions with two keys. Keying on the
         * rule alone would make an enable look like a replay of the disable before it. */
        idempotencyKeyFor(
          'notification-rule-enabled',
          `${change.ruleId}:${String(change.enabled)}`,
        ),
        { enabled: change.enabled },
      ),
    onSuccess: (_result, change) => {
      forgetIdempotencyKey(
        'notification-rule-enabled',
        `${change.ruleId}:${String(change.enabled)}`,
      );
    },
    /* `onSettled`, not `onSuccess`: a 404 means somebody else withdrew the rule, and the list is
     * stale either way. Refetching on failure is what stops the screen offering a control for a rule
     * the server has already forgotten. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
